import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Upload, RefreshCw } from "lucide-react";
import { toast } from "sonner";

function fileToDataUrl(f) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(f);
  });
}

export default function BookingPayment() {
  const { slug, rid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [reference, setReference] = useState("");
  const [amount, setAmount] = useState("");
  const [proof, setProof] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data: d } = await api.get(`/public/reservations/${rid}`);
      setData(d);
      // If reservation status is already "confirmed" (waived payment) — skip this page
      if (d?.reservation?.status === "confirmed") {
        nav(`/book/${slug}/confirmed/${rid}`, { replace: true });
      }
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rid]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Max 4 MB image"); return; }
    try { setProof(await fileToDataUrl(f)); } catch { toast.error("Couldn't read file"); }
  };

  const submit = async () => {
    if (!proof) return toast.error("Please upload your payment screenshot");
    setBusy(true);
    try {
      await api.post(`/public/reservations/${rid}/payment-upload`, {
        image_url: proof,
        payment_reference: reference,
        amount: Number(amount || 0),
      });
      toast.success("Payment uploaded");
      nav(`/book/${slug}/confirmed/${rid}`, { replace: true });
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  const r = data.reservation || {};
  const p = data.payment || {};

  return (
    <div className="min-h-screen bg-slate-50" data-testid="booking-payment">
      <header className="bg-gradient-to-r from-blue-800 to-cyan-500 text-white">
        <div className="max-w-3xl mx-auto px-6 py-7">
          <div className="text-xs tracking-[0.32em] text-white/80">eMedbay</div>
          <h1 className="text-2xl md:text-3xl font-extrabold mt-2" style={{ fontFamily: "Work Sans" }}>Complete your payment</h1>
          <p className="text-white/85 text-sm mt-1">{data.hospital_name} · {r.reservation_date} at {r.reservation_time}</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-6 grid md:grid-cols-2 gap-5">
        <section className="bg-white border border-slate-200 rounded-2xl p-5">
          <h2 className="font-bold text-slate-900">Scan to pay</h2>
          <div className="mt-3 flex items-center justify-center bg-slate-50 border border-slate-200 rounded-xl p-4 min-h-[260px]">
            {p.qr_image_url ? (
              <img src={p.qr_image_url} alt="Payment QR" className="max-h-72 object-contain" />
            ) : (
              <div className="text-sm text-slate-500 text-center">The clinic hasn’t configured their payment QR yet. Please contact the clinic.</div>
            )}
          </div>
          {p.payment_account_name ? <div className="mt-3 text-sm text-slate-700"><span className="font-semibold">Account: </span>{p.payment_account_name}</div> : null}
          {p.payment_instructions ? <div className="mt-1 text-sm text-slate-600 whitespace-pre-wrap">{p.payment_instructions}</div> : null}
          {p.consultation_fee_default ? <div className="mt-2 text-sm font-semibold text-blue-800">Amount due: ₱{Number(p.consultation_fee_default).toLocaleString()}</div> : null}
        </section>

        <section className="bg-white border border-slate-200 rounded-2xl p-5 space-y-3">
          <h2 className="font-bold text-slate-900">Upload your receipt</h2>
          <Input placeholder="Payment reference (optional)" value={reference} onChange={(e) => setReference(e.target.value)} className="h-11" />
          <Input placeholder="Amount paid (PHP)" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-11" />
          <div>
            <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="payment-proof-file" />
            <Button variant="outline" type="button" onClick={() => fileRef.current?.click()} className="w-full h-11">
              <Upload size={16} className="mr-1" />{proof ? "Replace screenshot" : "Choose screenshot"}
            </Button>
            {proof ? <img src={proof} alt="proof" className="mt-3 max-h-44 object-contain rounded-lg border" /> : null}
          </div>
          <Button onClick={submit} disabled={busy} className="w-full h-11 bg-blue-700 hover:bg-blue-800" data-testid="payment-submit">
            {busy ? "Uploading…" : "Submit payment proof"}
          </Button>
          <Button variant="ghost" type="button" onClick={load} className="w-full text-xs text-slate-500">
            <RefreshCw size={12} className="mr-1" />Reload reservation
          </Button>
        </section>
      </main>
    </div>
  );
}
